import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, XCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AuthSubmitButton } from './AuthSubmitButton';

interface LoginFormProps {
    onSubmit: (email: string, password: string) => Promise<void>;
    loading: boolean;
    error: string | null;
    onSwitchToSignUp: () => void;
    onForgotPassword?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, loading, error, onSwitchToSignUp, onForgotPassword }) => {
    const t = useTranslations('auth.login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await onSubmit(email, password);
    };

    return (
        <>
            <div className="mb-8 text-center">
                <h2 className="text-sm font-semibold text-fg mb-2">{t('title')}</h2>
                <p className="text-sm text-fg-secondary leading-relaxed max-w-[280px] mx-auto">{t('description')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">

                {/* Email field. The visible label is the accessible name; there is no
                    placeholder, so the name can never collapse into one. */}
                <div className="space-y-2">
                    <label htmlFor="login-email" className="block text-sm text-fg-secondary">
                        {t('email')}
                    </label>
                    <div className="relative group/input">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10 h-4 w-4">
                            <Mail className="w-full h-full text-fg-muted group-focus-within/input:text-fg transition-colors" />
                        </div>
                        <Input
                            id="login-email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="h-11 pl-11"
                        />
                    </div>
                </div>

                {/* Password field */}
                <div className="space-y-2">
                    <label htmlFor="login-password" className="block text-sm text-fg-secondary">
                        {t('password')}
                    </label>
                    <div className="relative group/input">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none z-10 h-4 w-4">
                            <Lock className="w-full h-full text-fg-muted group-focus-within/input:text-fg transition-colors" />
                        </div>
                        <Input
                            id="login-password"
                            type={showPassword ? "text" : "password"}
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="h-11 pl-11 pr-11"
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            aria-pressed={showPassword}
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-1 top-1/2 -translate-y-1/2 z-20 h-8 w-8 text-fg-muted hover:text-fg"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                    </div>
                </div>

                {onForgotPassword && (
                    <div className="text-right">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={onForgotPassword}
                            className="text-sm"
                        >
                            {t('forgotPassword')}
                        </Button>
                    </div>
                )}

                {/* An auth failure is announced, not just rendered. */}
                {error && (
                    <div role="alert" className="flex items-start gap-2 animate-in slide-in-from-top-2">
                        <span className="flex h-5 w-4 shrink-0 items-center">
                            <XCircle className="w-4 h-4 text-danger-text" />
                        </span>
                        <p className="text-sm text-danger-text leading-snug">{error}</p>
                    </div>
                )}

                <AuthSubmitButton loading={loading} className="mt-3">
                    {t('submit')}
                </AuthSubmitButton>
            </form>

            <div className="mt-8 text-center">
                <Button
                    variant="ghost"
                    onClick={onSwitchToSignUp}
                    className="text-[11px] font-semibold uppercase tracking-[0.09em]"
                >
                    {t('noAccount')} {t('signUp')}
                </Button>
            </div>
        </>
    );
};
